import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'src/lib/routing';
import { useAddUTMToAppState } from 'src/hooks/useAddUTMToAppState';

import { useConnectedClient } from 'context/ConnectedClientContext';
import { ResearcherCertificationStatus } from 'src/constants/graphql-enums';
import { userUtils } from 'lib/user';

import { useIsKinde } from './AuthWrapper';
import { ViewerErrorPage } from './ViewerErrorPage';

import type React from 'react';
import type { NavigateOptions } from 'src/lib/routing';

export const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, login } = useKindeAuth();
  const isKinde = useIsKinde();
  const navigate = useNavigate();
  const { addUTMToAppState } = useAddUTMToAppState();

  const { authState, details: clientDetails, isLoading, actions } = useConnectedClient();

  const location = useLocation();
  const isTrialEligibleRoute = location.pathname.startsWith('/streams');

  const isDemoClientUser = userUtils.isDemoClient(clientDetails?.roles);
  // Demo clients shouldn't use any private route
  if (isDemoClientUser) localStorage.reset();
  // Scroll to top

  useEffect(() => {
    if (authState !== 'unauthenticated') {
      return;
    }

    if (isTrialEligibleRoute) {
      return;
    }

    if (isKinde) {
      if (isAuthenticated) {
        return;
      }

      const searchParams = new URLSearchParams(location.search);

      const userType = searchParams.get('user_type') as 'researcher' | 'client' | null;

      if (!userType) {
        if (import.meta.env.VITE_ENVIRONMENT !== 'PROD') {
          navigate('/login-select');
          return;
        }

        window.location.replace('https://www.askable.com/login');
        return;
      }

      void login({
        state: addUTMToAppState({
          user_type: userType,
        }),
      });

      return;
    }

    // For non-kinde, don't redirect if still loading auth state
    if (isLoading) {
      return;
    }

    let stateTo: NavigateOptions['state'] = { from: location };
    if (location?.search) {
      stateTo = { from: { pathname: `${location.pathname}${location.search}` } };
    }

    navigate('/login', {
      state: stateTo,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs on auth and URL changes only; navigate and login are not stable
  }, [authState, isLoading, isTrialEligibleRoute, isKinde, isAuthenticated, location.pathname, location.search]);

  // Trial-eligible routes bypass authenticated-user redirects too — a logged-in user
  // landing on /streams should not be bounced to /researcher/certification or /teams
  // mid-flow. StreamsTrialGate handles the trial-specific UX from here.
  if (isTrialEligibleRoute) {
    return children;
  }

  // Kinde has signed the user in, so no Viewer after loading means the Viewer query failed or was empty.
  if (isKinde && isAuthenticated && !isLoading && authState !== 'authenticated') {
    return <ViewerErrorPage onRetry={actions.refetch} />;
  }

  const isTeamSelectionRoute = location.pathname.startsWith('/teams');
  // Membership, not the selected ConnectedTeam (team.id): a freshly-joined Kinde user has no
  // ConnectedTeam until the org-switch redirect lands.
  const hasClientTeam = (clientDetails?.Teams?.length ?? 0) > 0;

  // Send uncertified researchers to certification — unless already there (loop guard), accepting
  // an invite on /teams, or already a member of a client team.
  if (
    location.pathname !== '/researcher/certification' &&
    !isTeamSelectionRoute &&
    clientDetails?.type?.researcher &&
    !hasClientTeam &&
    clientDetails?.researcher?.certification?.status !== ResearcherCertificationStatus.Certified
  ) {
    return (
      <Navigate
        replace
        to={{
          pathname: '/researcher/certification',
        }}
      />
    );
  }

  if (
    clientDetails &&
    !clientDetails.type?.researcher &&
    !clientDetails.team?.id &&
    !location.pathname.includes('/teams')
  ) {
    return (
      <Navigate
        replace
        to={{
          pathname: '/teams',
        }}
      />
    );
  }

  if (authState !== 'authenticated') {
    return null;
  }

  return children;
};
